import React, { useRef, useEffect, useState, useCallback } from 'react';

// Wraps the daily-view timeline with edge affordances on whichever side has
// more content: a gradient fade (passive cue) and an LCARS-style pill button
// that scrolls the timeline when clicked. Both auto-hide at the edges.
export default function ScrollableTimeline({ children }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < max - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    // Re-check when the timeline's own content resizes (e.g. data fetch
    // populates slots after mount, font loads shift widths, etc.).
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateScrollState)
      : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      ro?.disconnect();
    };
  }, [updateScrollState]);

  const scrollByAmount = (delta) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div className="schedule-timeline-wrap">
      <div className="schedule-timeline" ref={scrollRef}>
        {children}
      </div>
      {canScrollLeft && (
        <>
          <div className="schedule-scroll-fade schedule-scroll-fade-left" aria-hidden="true" />
          <button
            type="button"
            className="schedule-scroll-cue schedule-scroll-cue-left"
            onClick={() => scrollByAmount(-320)}
            aria-label="Scroll timeline left"
          >
            ‹
          </button>
        </>
      )}
      {canScrollRight && (
        <>
          <div className="schedule-scroll-fade schedule-scroll-fade-right" aria-hidden="true" />
          <button
            type="button"
            className="schedule-scroll-cue schedule-scroll-cue-right"
            onClick={() => scrollByAmount(320)}
            aria-label="Scroll timeline right"
          >
            ›
          </button>
        </>
      )}
    </div>
  );
}
